import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { TrackingConsentService } from './tracking-consent.service';

interface TrackingWindow extends Window {
  gtag?: (...args: unknown[]) => void;
  lintrk?: (event: string, payload: Record<string, unknown>) => void;
}

@Injectable({
  providedIn: 'root'
})
export class TrackingService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly trackingConsentService = inject(TrackingConsentService);

  public trackEvent(
    name: string,
    params?: Record<string, unknown>,
    category: 'analytics' | 'marketing' = 'analytics'
  ): void {
    this.trackingConsentService.initialize();

    if (category === 'analytics' && !this.trackingConsentService.hasAnalyticsConsent()) {
      return;
    }
    if (category === 'marketing' && !this.trackingConsentService.hasMarketingConsent()) {
      return;
    }

    const windowRef = this.getWindow();
    if (!windowRef?.gtag) {
      return;
    }

    const googleTagStatus = this.trackingConsentService.getGoogleTagStatus();
    if (googleTagStatus !== 'loaded') {
      const message =
        googleTagStatus === 'failed'
          ? '[Tracking] Google tag script failed to load. Event will stay in dataLayer and no network request will be sent.'
          : '[Tracking] Google tag script is not loaded yet. Event is being queued in dataLayer until the script becomes available.';
      console.warn(message, { name, params, category, googleTagStatus });
    }

    windowRef.gtag('event', name, params ?? {});
  }

  public trackPageView(path: string): void {
    this.trackEvent('page_view', { page_path: path });
  }

  public trackLinkedInConversion(conversionId: number): void {
    this.trackingConsentService.initialize();

    if (!this.trackingConsentService.hasMarketingConsent()) {
      return;
    }

    const windowRef = this.getWindow();
    if (!windowRef?.lintrk) {
      return;
    }

    windowRef.lintrk('track', { conversion_id: conversionId });
  }

  private getWindow(): TrackingWindow | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    return this.document.defaultView as TrackingWindow | null;
  }
}
